import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import {
  useGetAssessmentActivitiesByParentId,
  useGetAssessmentRCSAActivitiesByAssessmentId,
} from 'src/hooks/queries';
import type { ObjectWithContributors } from 'src/rbac/Permission';

import { useDeleteAssessmentActivities } from '@/hooks/mutations/assessment-activity/useDeleteAssessmentActivities';
import { useDeleteWizard } from '@/hooks/mutations/wizard';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { handleError } from '@/utils/errorUtils';

import type { AssessmentTypeEnum } from '../../../types';
import { ActivitiesRegister } from './ActivitiesRegister';
import { RCSAActivitiesRegister } from './RCSAActivitiesRegister';
import { useActivitiesStore } from './store/useActivitiesStore';
import { DeleteTypeEnum } from './types';

const translationKeyPrefix = 'assessmentActivities';

interface Props {
  parent: ObjectWithContributors;
  assessmentMode: AssessmentTypeEnum;
}

const Tab: FC<Props> = ({ parent, assessmentMode }) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: translationKeyPrefix,
  });

  const assessmentId = useGetGuidParam('assessmentId');

  const {
    setIsActivityDeleteModalVisible,
    isActivityDeleteModalVisible,
    setSelectedActivities,
    selectedActivities,
    deleteType,
    selectedRCSAActivities,
    setSelectedRCSAActivities,
  } = useActivitiesStore();

  const {
    data,
    loading: activitiesLoading,
    refetch,
  } = useGetAssessmentActivitiesByParentId({
    queryArgs: { id: assessmentId },
  });

  const { deleteWizard } = useDeleteWizard();

  const { deleteAssessmentActivities, loading: deleteLoading } =
    useDeleteAssessmentActivities();

  const wizardFeatureEnabled = useIsModuleEnabled(
    'risk.subModules.rcsa_wizard'
  );

  const showRCSAActivities =
    wizardFeatureEnabled && assessmentMode === 'rating';

  // The RCSA table reads a DIFFERENT query from the activities table, so the
  // `refetch` above does not refresh it. It used to live inside
  // `RCSAActivitiesRegister`, out of reach of this delete handler, which left
  // deleted RCSA rows on screen until remount (COI-820). Skipped unless the
  // register is actually rendered, so the request pattern is unchanged when the
  // wizard sub-module is off or the assessment isn't in rating mode.
  const {
    data: rcsaActivitiesData,
    loading: rcsaActivitiesLoading,
    refetch: refetchRCSAActivities,
  } = useGetAssessmentRCSAActivitiesByAssessmentId({
    queryArgs: { assessmentId },
    shouldSkip: !showRCSAActivities,
  });

  // The rows are already deleted by the time this runs, so a failed refresh is
  // a stale table — not a failed delete. Letting it reject `asyncAction` would
  // toast "Failed to save changes" for a delete that succeeded.
  const refreshRegisters = async (isRCSARefreshRequired: boolean) => {
    try {
      await refetch();
      if (isRCSARefreshRequired) {
        await refetchRCSAActivities();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (deleteType === DeleteTypeEnum.Activity) {
        await deleteAssessmentActivities(selectedActivities.map((s) => s.Id));
        setSelectedActivities([]);
        setIsActivityDeleteModalVisible(false);
        await refreshRegisters(false);

        return true;
      } else if (deleteType === DeleteTypeEnum.RCSA) {
        await Promise.all(
          selectedRCSAActivities.map(async (rcsaActivity) => {
            if (!rcsaActivity.RiskId) {
              return;
            }
            await deleteWizard({ RiskId: rcsaActivity.RiskId });
          })
        );
        await deleteAssessmentActivities(
          selectedRCSAActivities.map((s) => s.Id)
        );
        setSelectedRCSAActivities([]);
        setIsActivityDeleteModalVisible(false);
        await refreshRegisters(true);

        return true;
      }

      return false;
    },
    failureAction: () => {
      setIsActivityDeleteModalVisible(false);
    },
  });

  return (
    <>
      <ActivitiesRegister
        assessmentMode={assessmentMode}
        parent={parent}
        data={data}
        loading={activitiesLoading}
      ></ActivitiesRegister>
      {showRCSAActivities && (
        <RCSAActivitiesRegister
          parent={parent}
          data={rcsaActivitiesData}
          loading={rcsaActivitiesLoading}
        ></RCSAActivitiesRegister>
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isActivityDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsActivityDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default Tab;
